import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, HardHat, Hammer, Zap, Truck, Wrench, Flame, Shield, Clock, MapPin, Star, Quote, Euro, Rocket, Radio } from "lucide-react";
import { api } from "@/lib/api";
import ActivityTicker from "@/components/ActivityTicker";
import SignupTicker from "@/components/SignupTicker";
import FoundingBanner from "@/components/FoundingBanner";
import BoostBanner from "@/components/BoostBanner";
import TradeChips from "@/components/TradeChips";

const HERO_IMG = "https://images.pexels.com/photos/8961260/pexels-photo-8961260.jpeg";
const WELDING_IMG = "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1";
const TRUCK_IMG = "https://images.unsplash.com/photo-1574757974346-45bae947d89a";
const CONSTRUCTION_IMG = "https://images.unsplash.com/photo-1646324554833-f0b6a479fa5d";

const TRADES = [
  { name: "Construction", Icon: HardHat, img: CONSTRUCTION_IMG },
  { name: "Steel Erector", Icon: Hammer, img: CONSTRUCTION_IMG },
  { name: "Electrician", Icon: Zap },
  { name: "Truck Driver", Icon: Truck, img: TRUCK_IMG },
  { name: "Plumber", Icon: Wrench },
  { name: "Welder", Icon: Flame, img: WELDING_IMG },
];

export default function Landing() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data.slice(0, 6))).catch(() => {});
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  function timeAgo(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.max(1, Math.floor((now - then) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  }

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative border-b border-neutral-800 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-20 md:pt-28 md:pb-32 grain">
          <div className="max-w-3xl">
            {/* Live activity ticker */}
            {stats && (
              <div data-testid="live-activity-bar" className="mb-6 inline-flex items-center gap-3 bg-neutral-900 border border-neutral-800 px-3 py-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                </span>
                <span className="text-[10px] uppercase tracking-widest text-neutral-300">
                  <span className="text-amber-500 font-bold">LIVE</span>
                  <span className="mx-2 text-neutral-700">·</span>
                  <span className="text-neutral-100 font-bold">{stats.jobs_live}</span> jobs live
                  <span className="mx-2 text-neutral-700">·</span>
                  <span className="text-neutral-100 font-bold">{stats.workers_available}</span> workers
                  {stats.jobs_posted_24h > 0 && (
                    <>
                      <span className="mx-2 text-neutral-700">·</span>
                      <span className="text-amber-500 font-bold">{stats.jobs_posted_24h}</span> posted today
                    </>
                  )}
                </span>
              </div>
            )}
            {/* Social proof counter — tradies joined this week */}
            {stats && (stats.workers_joined_week > 0 || stats.employers_joined_week > 0) && (
              <div
                data-testid="joined-week-counter"
                className="mb-6 inline-flex items-center gap-4 bg-gradient-to-r from-amber-500/15 to-transparent border-l-2 border-amber-500 pl-4 pr-5 py-3"
              >
                <Rocket className="w-5 h-5 text-amber-500 shrink-0" />
                <div>
                  <div className="flex items-baseline gap-2">
                    <span
                      data-testid="joined-week-count"
                      className="display text-3xl md:text-4xl text-amber-500 leading-none"
                    >
                      {stats.workers_joined_week + stats.employers_joined_week}
                    </span>
                    <span className="text-neutral-100 text-sm md:text-base font-bold uppercase tracking-widest">
                      joined this week
                    </span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">
                    <span className="text-amber-500 font-bold">{stats.workers_joined_week}</span> tradies
                    <span className="mx-2 text-neutral-700">·</span>
                    <span className="text-amber-500 font-bold">{stats.employers_joined_week}</span> contractors
                    <span className="mx-2 text-neutral-700">·</span>
                    Don't miss out
                  </div>
                </div>
              </div>
            )}
            <SignupTicker />
            <BoostBanner />
            <TradeChips />
            <FoundingBanner />
            <div className="flex items-center gap-2 mb-6">
              <div className="h-[2px] w-10 bg-amber-500" />
              <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">
                Ireland &amp; UK · Trades Marketplace
              </span>
            </div>
            <h1 className="display text-5xl sm:text-7xl lg:text-8xl">
              Boots on <br />
              <span className="text-amber-500">the ground.</span>
              <br />
              Fast.
            </h1>
            <p className="mt-8 max-w-xl text-neutral-300 text-lg leading-relaxed">
              SiteCrews connects Irish contractors with skilled trades for
              weekend shifts, weeks, or long jobs. No middle-men. No agency
              fees. Real workers. Real jobs.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/hire-crew"
                data-testid="hero-employer-cta"
                className="group flex items-center justify-between gap-6 px-6 py-5 bg-amber-500 text-neutral-950 hover:bg-amber-400 transition-colors"
              >
                <div className="text-left">
                  <div className="text-xs font-bold uppercase tracking-widest opacity-70">
                    Contractors
                  </div>
                  <div className="display text-2xl">I Need Workers</div>
                </div>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/get-work"
                data-testid="hero-worker-cta"
                className="group flex items-center justify-between gap-6 px-6 py-5 bg-neutral-900 border border-neutral-800 hover:border-amber-500 transition-colors"
              >
                <div className="text-left">
                  <div className="text-xs font-bold uppercase tracking-widest text-amber-500">
                    Tradespeople
                  </div>
                  <div className="display text-2xl">I Need Work</div>
                </div>
                <ArrowRight className="w-6 h-6 text-amber-500 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-neutral-800 border border-neutral-800">
            {[
              ["26", "Counties"],
              ["09", "Trades"],
              ["24h", "Fill Time"],
              ["0€", "Agency Fees"],
            ].map(([n, l]) => (
              <div key={l} className="bg-neutral-950 px-6 py-6">
                <div className="display text-4xl text-amber-500">{n}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-400 mt-1">
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE ACTIVITY TICKER */}
      <ActivityTicker />

      {/* LIVE JOBS - trust builder */}
      {jobs.length > 0 && (
        <section data-testid="live-jobs-section" className="border-b border-neutral-800 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                  <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Live jobs · Right now</span>
                </div>
                <h2 className="display text-4xl md:text-5xl">Real work. <span className="text-amber-500">Posted today.</span></h2>
                <p className="text-neutral-400 text-sm mt-3 max-w-lg">
                  These employers are actively looking for crew. Ring them direct — no agencies, no middlemen.
                </p>
              </div>
              <Link
                to="/jobs"
                data-testid="see-all-jobs"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-neutral-400 hover:text-amber-500 transition-colors"
              >
                See all {stats?.jobs_live || jobs.length} jobs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800">
              {jobs.slice(0, 6).map((j) => (
                <Link
                  key={j.id}
                  to={`/jobs/${j.id}`}
                  data-testid={`live-job-${j.id}`}
                  className={`group bg-neutral-950 hover:bg-neutral-900 transition-colors p-6 flex flex-col ${j.is_boosted ? "border-l-2 border-amber-500 bg-amber-500/5" : ""}`}
                >
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <div className="text-xs font-bold uppercase tracking-widest text-amber-500">{j.trade}</div>
                    {j.is_boosted && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500 text-neutral-950 text-[9px] font-bold uppercase tracking-widest">
                        <Flame className="w-2.5 h-2.5" /> Urgent
                      </span>
                    )}
                    <span className="ml-auto text-[10px] uppercase tracking-widest text-neutral-500">
                      {timeAgo(j.created_at)}
                    </span>
                  </div>
                  <h3 className="display text-xl group-hover:text-amber-500 transition-colors line-clamp-2">{j.title}</h3>
                  <div className="mt-4 flex flex-wrap gap-4 text-xs text-neutral-400">
                    <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-amber-500" />{j.county}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-amber-500" />{j.duration}</span>
                    {j.day_rate ? (
                      <span className="inline-flex items-center gap-1.5 text-amber-400 font-bold">
                        <Euro className="w-3.5 h-3.5" />{j.day_rate}/day
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-6 pt-4 border-t border-neutral-800 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">Ring direct</span>
                    <ArrowRight className="w-4 h-4 text-neutral-500 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              ))}
            </div>

            {/* Trust strip below jobs */}
            <div className="mt-8 flex items-center justify-center gap-6 flex-wrap text-xs uppercase tracking-widest text-neutral-500">
              <span className="inline-flex items-center gap-2 text-neutral-300">
                <Radio className="w-3.5 h-3.5 text-amber-500 animate-pulse" />
                <span className="text-amber-500 font-bold">{stats?.jobs_posted_24h || 0}</span> jobs posted in the last 24 hours
              </span>
              <span className="hidden md:inline">·</span>
              <span className="inline-flex items-center gap-2 text-neutral-300">
                <Rocket className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-amber-500 font-bold">First boost FREE</span> for new employers
              </span>
            </div>
          </div>
        </section>
      )}

      {/* MARQUEE COUNTIES */}
      <section className="border-b border-neutral-800 overflow-hidden bg-neutral-900">
        <div className="flex whitespace-nowrap ticker-track">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 py-4">
              {["Dublin", "Cork", "Galway", "Limerick", "Waterford", "Meath", "Louth", "Kilkenny", "Wexford", "Kildare", "Wicklow", "Weekend Shifts", "Weekly Work", "Monthly Contracts", "Ongoing"].map((t) => (
                <div key={t + i} className="flex items-center gap-4">
                  <div className="w-1 h-1 bg-amber-500" />
                  <span className="display text-2xl text-neutral-400">{t}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* TRADES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">
              Trades on site
            </div>
            <h2 className="display text-4xl md:text-5xl mt-3">Every Skill. Every Shift.</h2>
          </div>
          <Link
            to="/workers"
            data-testid="see-all-workers"
            className="hidden md:inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-neutral-400 hover:text-amber-500 transition-colors"
          >
            Browse Crew <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800">
          {TRADES.map(({ name, Icon, img }) => (
            <Link
              key={name}
              to={`/jobs?trade=${encodeURIComponent(name)}`}
              data-testid={`trade-card-${name.replace(/\s+/g, "-").toLowerCase()}`}
              className="group relative bg-neutral-950 p-8 min-h-[180px] flex flex-col justify-between hover:bg-neutral-900 transition-colors"
            >
              {img && (
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity"
                  style={{ backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
              )}
              <div className="relative">
                <Icon className="w-8 h-8 text-amber-500" strokeWidth={2} />
              </div>
              <div className="relative flex items-end justify-between">
                <div className="display text-2xl md:text-3xl">{name}</div>
                <ArrowRight className="w-5 h-5 text-neutral-600 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase mb-3">For Contractors</div>
              <h3 className="display text-4xl mb-6">Post. Match. Hire.</h3>
              <ol className="space-y-6">
                {[
                  ["01", "Post the shift", "Trade, county, dates, day rate. Two minutes."],
                  ["02", "See applicants", "Verified workers apply. Skim their headline & years on the tools."],
                  ["03", "Get contact", "Phone & email unlock instantly on their application."],
                ].map(([n, t, d]) => (
                  <li key={n} className="flex gap-4 pb-6 border-b border-neutral-800 last:border-0">
                    <div className="display text-4xl text-amber-500 w-16 shrink-0">{n}</div>
                    <div>
                      <div className="display text-xl">{t}</div>
                      <div className="text-neutral-400 text-sm mt-1">{d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="md:border-l md:border-neutral-800 md:pl-12">
              <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase mb-3">For Workers</div>
              <h3 className="display text-4xl mb-6">List. Get Called. Get Paid.</h3>
              <ol className="space-y-6">
                {[
                  ["01", "Post availability", "Trade, county, day rate, when you're free."],
                  ["02", "Get discovered", "Contractors browse your profile and reach out."],
                  ["03", "Show up ready", "Confirm details. Bring the tools. Get on site."],
                ].map(([n, t, d]) => (
                  <li key={n} className="flex gap-4 pb-6 border-b border-neutral-800 last:border-0">
                    <div className="display text-4xl text-amber-500 w-16 shrink-0">{n}</div>
                    <div>
                      <div className="display text-xl">{t}</div>
                      <div className="text-neutral-400 text-sm mt-1">{d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-t border-neutral-800 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid md:grid-cols-3 gap-6">
          {[
            [Shield, "Direct hire, zero agency fees"],
            [Clock, "Fill shifts within 24 hours"],
            [MapPin, "Live across Ireland &amp; the UK — 53 counties and regions"],
          ].map(([Icon, t]) => (
            <div key={t} className="flex items-center gap-4">
              <div className="w-10 h-10 bg-amber-500 grid place-items-center shrink-0">
                <Icon className="w-5 h-5 text-neutral-950" strokeWidth={2.5} />
              </div>
              <div className="text-sm uppercase tracking-wide text-neutral-300">{t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="border-t border-neutral-800 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-[2px] w-10 bg-amber-500" />
            <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">On-site voices</span>
          </div>
          <h2 className="display text-4xl md:text-6xl max-w-3xl">
            Trusted by <span className="text-amber-500">crews across Ireland &amp; UK.</span>
          </h2>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800">
            {[
              {
                quote: "Posted a weekend welder job on Friday morning. Had three coded lads on site by Saturday. Would have taken a week through an agency.",
                name: "Sean McBride",
                role: "Site Manager · McBride Civils",
                loc: "Dublin",
              },
              {
                quote: "Ditched the agency after they took 30% off my day rate. On SiteCrews I set my own rate and the contractor rings me direct. Been on solid work since.",
                name: "Paddy Murphy",
                role: "Plumber (RGI)",
                loc: "Dublin",
              },
              {
                quote: "€9 a month and I've had steady work for 6 weeks running. Best money I've spent all year.",
                name: "Marek Kowalski",
                role: "Coded Welder",
                loc: "Meath",
              },
              {
                quote: "We run a small crew — Employer Pro paid for itself in one hire. Cheaper than half a day's agency fee.",
                name: "Aoife O'Sullivan",
                role: "Owner · Northeast Build",
                loc: "Louth",
              },
              {
                quote: "Had a teleporter driver lined up in 4 hours. Straight through to the man, no messing.",
                name: "Cathal Lynch",
                role: "Foreman · Royal Civils",
                loc: "Meath",
              },
              {
                quote: "Been on the tools 22 years. Never seen anything cut out the middleman like this. Simple as it gets.",
                name: "Tomás Ó Briain",
                role: "Steel Erector",
                loc: "Cork",
              },
            ].map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-neutral-950 p-8 flex flex-col justify-between gap-6 hover:bg-neutral-900 transition-colors"
              >
                <div>
                  <Quote className="w-6 h-6 text-amber-500 mb-4" strokeWidth={2.5} />
                  <p className="text-neutral-200 leading-relaxed text-[15px]">&ldquo;{t.quote}&rdquo;</p>
                </div>
                <div className="border-t border-neutral-800 pt-4">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <div className="text-neutral-100 font-bold text-sm">{t.name}</div>
                  <div className="text-neutral-500 text-xs uppercase tracking-widest mt-0.5">
                    {t.role} · {t.loc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center justify-center gap-6 text-neutral-500 text-xs uppercase tracking-widest">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
              <span className="text-neutral-300"><span className="text-amber-500 font-bold">4.9</span> average from 200+ crews</span>
            </div>
            <span className="hidden md:inline">·</span>
            <span>All 26 IE counties + 27 UK regions</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 text-center">
          <h2 className="display text-4xl md:text-6xl">
            Ready to <span className="text-amber-500">get on site?</span>
          </h2>
          <p className="text-neutral-400 mt-4 max-w-xl mx-auto">
            Join Ireland &amp; UK&apos;s fastest-growing labour marketplace. Sign up in under a minute.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link
              to="/register"
              data-testid="cta-signup-btn"
              className="px-6 py-4 bg-amber-500 text-neutral-950 font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors"
            >
              Create Free Account
            </Link>
            <Link
              to="/jobs"
              data-testid="cta-browse-btn"
              className="px-6 py-4 border border-neutral-800 text-neutral-100 font-bold uppercase tracking-widest hover:border-amber-500 hover:text-amber-500 transition-colors"
            >
              Browse Jobs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
